import { useCallback, useMemo, useRef, useState } from 'react'
import { isValidRow } from './bulkStatus.helpers'
import type { ParsedRow } from './bulkStatus.types'
import type { PaymentAssignmentStatusId } from '../PaymentAssignment/Utils/types'

/**
 * Encapsulates "jump to invalid row" navigation for the preview table:
 * tracks which invalid row is currently focused and scrolls it into view.
 * Clicking the invalid badge cycles through rows with validation errors only (excludes duplicates).
 */
export function useInvalidRowNavigation(
  rows: ParsedRow[],
  duplicateBenefitIds: ReadonlySet<string>,
  allowedStatuses?: ReadonlyArray<PaymentAssignmentStatusId>
) {
  const [focusedInvalidIdx, setFocusedInvalidIdx] = useState<number | null>(
    null
  )
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const invalidKeys = useMemo(
    () =>
      rows
        .filter((r) => {
          // Exclude duplicates - they should only be navigated via duplicate badge
          if (duplicateBenefitIds.has(r.benefitId.trim().toLowerCase())) {
            return false
          }
          // Include only rows with validation errors (not duplicates)
          return !isValidRow(r, duplicateBenefitIds, allowedStatuses)
        })
        .map((r) => r.key),
    [rows, duplicateBenefitIds, allowedStatuses]
  )

  // Jump to a specific invalid row: compute its page and scroll it into view
  const jumpToInvalid = useCallback(
    (idx: number) => {
      const key = invalidKeys[idx]
      if (!key) return
      setFocusedInvalidIdx(idx)
      // Scroll the row into view after a tick (let React re-render the highlight)
      setTimeout(() => {
        const container = tableContainerRef.current
        if (!container) return
        // Ant Design sets data-row-key on <tr> elements
        const tr = container.querySelector<HTMLElement>(
          `tr[data-row-key="${key}"]`
        )
        if (tr) tr.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 50)
    },
    [invalidKeys]
  )

  const handleInvalidTagClick = useCallback(() => {
    if (invalidKeys.length === 0) return
    // Cycle to next invalid row, or start from beginning if at the end
    const nextIdx =
      focusedInvalidIdx === null || focusedInvalidIdx >= invalidKeys.length - 1
        ? 0
        : focusedInvalidIdx + 1
    jumpToInvalid(nextIdx)
  }, [invalidKeys, focusedInvalidIdx, jumpToInvalid])

  const clearInvalidFocus = useCallback(() => {
    setFocusedInvalidIdx(null)
  }, [])

  const focusedKey =
    focusedInvalidIdx !== null ? invalidKeys[focusedInvalidIdx] : null

  return {
    tableContainerRef,
    focusedKey,
    handleInvalidTagClick,
    clearInvalidFocus,
  }
}
